{
  "schemaVersion": 1,
  "generatedAt": "2026-08-13T00:00:00.000Z",
  "integrations": [
    {
      "schemaVersion": 1,
      "id": "cloudflare",
      "name": "Cloudflare",
      "description": "Connect AI agents to Cloudflare zones, DNS records, and cache operations through a local MCP server.",
      "version": "0.1.1",
      "provider": "Cloudflare",
      "category": "infrastructure",
      "packageName": "@boolink-dev/cloudflare",
      "repositoryUrl": "https://github.com/ColinRM000/boolink",
      "documentationUrl": "https://boolink.dev/integrations/cloudflare",
      "verification": "official",
      "authentication": {
        "type": "bearer-token",
        "instructionsUrl": "https://developers.cloudflare.com/fundamentals/api/get-started/create-token/",
        "requirements": [
          {
            "id": "token",
            "label": "Cloudflare API token",
            "description": "An API token restricted to the zones and Zone Read, DNS Read or Write, and Cache Purge permissions the user explicitly intends to expose.",
            "source": "environment",
            "environmentVariables": ["CLOUDFLARE_API_TOKEN"],
            "required": true
          }
        ]
      },
      "transports": ["stdio"],
      "tools": [
        {
          "name": "cloudflare.verify_token",
          "title": "Verify Cloudflare API token",
          "description": "Verifies the locally configured Cloudflare API token and returns only its identifier, status, and validity dates. Use it before other operations to confirm authentication. It accepts no inputs and has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": []
        },
        {
          "name": "cloudflare.list_zones",
          "title": "List Cloudflare zones",
          "description": "Lists zones visible to the local token with optional name, status, account, and pagination filters. Use it to discover the exact zone ID needed by DNS and cache tools. It has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Zone: Read"]
        },
        {
          "name": "cloudflare.get_zone",
          "title": "Get Cloudflare zone",
          "description": "Returns one Cloudflare zone by its exact 32-character ID, including status, type, nameservers, account summary, and timestamps. Use it to inspect a known zone. It has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Zone: Read"]
        },
        {
          "name": "cloudflare.list_dns_records",
          "title": "List Cloudflare DNS records",
          "description": "Lists DNS records in one zone with bounded type, name, content, proxy, and pagination filters. Use it to inspect existing records and obtain exact record IDs before any change. It has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["DNS: Read"]
        },
        {
          "name": "cloudflare.get_dns_record",
          "title": "Get Cloudflare DNS record",
          "description": "Returns one DNS record by exact zone and record IDs. Use it to review the current name, value, TTL, proxy state, comment, and tags before changing or deleting a record. It has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["DNS: Read"]
        },
        {
          "name": "cloudflare.create_dns_record",
          "title": "Create Cloudflare DNS record",
          "description": "Creates a DNS record and can immediately change how a hostname resolves or is proxied. Use it only after the user reviews the exact zone, type, name, content, TTL, and proxy state. Repeated calls can create conflicting records.",
          "capabilities": ["create", "administrative"],
          "destructive": false,
          "idempotent": false,
          "requiredScopes": ["DNS: Write"]
        },
        {
          "name": "cloudflare.update_dns_record",
          "title": "Update Cloudflare DNS record",
          "description": "Updates supplied fields on one exact DNS record and can redirect traffic or change proxy behavior. Use it only after reading the current record and reviewing every change. At least one field is required; this is destructive and administrative.",
          "capabilities": ["modify", "administrative"],
          "destructive": true,
          "idempotent": true,
          "requiredScopes": ["DNS: Write"]
        },
        {
          "name": "cloudflare.delete_dns_record",
          "title": "Delete Cloudflare DNS record",
          "description": "Permanently deletes one DNS record by exact zone and record IDs, which can make services unreachable. Use it only after the user reviews the current record and explicitly intends its removal. This action is destructive.",
          "capabilities": ["delete", "administrative"],
          "destructive": true,
          "idempotent": true,
          "requiredScopes": ["DNS: Write"]
        },
        {
          "name": "cloudflare.purge_cache_urls",
          "title": "Purge Cloudflare cache URLs",
          "description": "Purges up to 30 exact HTTP or HTTPS URLs from one zone cache so following requests fetch fresh content. Prefer this bounded tool over a full purge. It changes production cache state, may increase origin load, and is destructive.",
          "capabilities": ["modify", "administrative"],
          "destructive": true,
          "idempotent": true,
          "requiredScopes": ["Cache Purge"]
        },
        {
          "name": "cloudflare.purge_everything",
          "title": "Purge all Cloudflare zone cache",
          "description": "Purges every cached object for one zone and can cause a substantial temporary increase in origin traffic. Use it only when a targeted URL purge cannot solve the problem and the user explicitly confirms the full-zone effect. This action is destructive.",
          "capabilities": ["modify", "administrative"],
          "destructive": true,
          "idempotent": true,
          "requiredScopes": ["Cache Purge"]
        }
      ]
    },
    {
      "schemaVersion": 1,
      "id": "github",
      "name": "GitHub",
      "description": "Connect AI agents to GitHub repositories, issues, and pull requests through a local MCP server.",
      "version": "0.2.1",
      "provider": "GitHub",
      "category": "development",
      "packageName": "@boolink-dev/github",
      "repositoryUrl": "https://github.com/ColinRM000/boolink",
      "documentationUrl": "https://boolink.dev/integrations/github",
      "verification": "official",
      "authentication": {
        "type": "bearer-token",
        "instructionsUrl": "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens",
        "requirements": [
          {
            "id": "token",
            "label": "GitHub token",
            "description": "A fine-grained personal access token limited to the repositories and read or write capabilities you explicitly intend to expose.",
            "source": "environment",
            "environmentVariables": ["GITHUB_TOKEN"],
            "required": true
          }
        ]
      },
      "transports": ["stdio"],
      "tools": [
        {
          "name": "github.get_authenticated_user",
          "title": "Get authenticated GitHub user",
          "description": "Returns the GitHub account associated with the locally configured token. Use it to confirm which identity BooLink will act as. It has no side effects and accepts no inputs.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": []
        },
        {
          "name": "github.search_issues",
          "title": "Search GitHub issues",
          "description": "Searches GitHub issues using GitHub search syntax. Use it to find issues across repositories visible to the configured token. Results exclude pull requests, are paginated, and this tool has no side effects. The query is required.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Issues: read for private repositories"]
        },
        {
          "name": "github.get_issue",
          "title": "Get a GitHub issue",
          "description": "Returns one issue by repository and number. Use it when an exact issue is known and full issue details are needed. GitHub may identify a pull request through this endpoint; the result marks that case. It has no side effects.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Issues: read for private repositories"]
        },
        {
          "name": "github.list_issue_comments",
          "title": "List GitHub issue or pull request comments",
          "description": "Lists timeline comments on one GitHub issue or pull request. Use it to read the public conversation before replying or changing the item. Results are paginated, it has no side effects, and owner, repository, and issue number are required.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": [
            "Issues: read for private issues or Pull requests: read for private pull requests"
          ]
        },
        {
          "name": "github.create_issue",
          "title": "Create a GitHub issue",
          "description": "Creates a new issue and triggers GitHub notifications. Use it only when the user intends to publish a new repository issue and has reviewed the title, body, labels, and assignees. This has an external communication side effect, requires owner, repository, and title, and cannot be automatically deduplicated.",
          "capabilities": ["create", "communication"],
          "destructive": false,
          "idempotent": false,
          "requiredScopes": ["Issues: write"]
        },
        {
          "name": "github.update_issue",
          "title": "Update a GitHub issue",
          "description": "Changes fields on an existing GitHub issue, including its title, body, state, labels, or assignees, and may trigger notifications. Use it only when the user intends to modify that exact issue and has reviewed every supplied field. It can close an issue or replace label and assignee sets, so it is classified as destructive and requires at least one change.",
          "capabilities": ["modify", "communication"],
          "destructive": true,
          "idempotent": true,
          "requiredScopes": ["Issues: write"]
        },
        {
          "name": "github.add_issue_comment",
          "title": "Comment on a GitHub issue or pull request",
          "description": "Publishes a timeline comment on an existing GitHub issue or pull request and triggers notifications. Use it only when the user intends to communicate the exact supplied text publicly to that repository conversation. Owner, repository, issue number, and a non-empty body are required, and repeated calls create duplicate comments.",
          "capabilities": ["create", "communication"],
          "destructive": false,
          "idempotent": false,
          "requiredScopes": ["Issues: write or Pull requests: write"]
        },
        {
          "name": "github.list_pull_requests",
          "title": "List GitHub pull requests",
          "description": "Lists pull requests in one repository with state, branch, sort, and pagination filters. Use it to inspect repository collaboration activity. It has no side effects; owner and repository are required.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Pull requests: read for private repositories"]
        },
        {
          "name": "github.get_pull_request",
          "title": "Get a GitHub pull request",
          "description": "Returns one pull request by repository and number, including its state, branches, author, draft status, and timestamps. Use it when an exact pull request is known and detailed metadata is needed. It has no side effects and requires owner, repository, and pull request number.",
          "capabilities": ["read"],
          "destructive": false,
          "idempotent": true,
          "requiredScopes": ["Pull requests: read for private repositories"]
        },
        {
          "name": "github.create_pull_request",
          "title": "Create a GitHub pull request",
          "description": "Creates a pull request between existing branches and triggers GitHub notifications. Use it only when commits are already pushed and the user intends to publish the exact title, body, head, base, and draft status. This has an external communication side effect and repeated calls may fail or create duplicate proposals.",
          "capabilities": ["create", "communication"],
          "destructive": false,
          "idempotent": false,
          "requiredScopes": ["Pull requests: write"]
        }
      ]
    }
  ]
}
